/**
 * One answer row of the clarifying-question card — the onboarding budget radio
 * row (Onboarding.jsx:228-250) in production tokens: 44px+, `--g-radius-sm`,
 * selected = soft fill + primary border + primary-ink text, an 18px ring (a
 * dot when chosen) or a 4px-radius box (a tick) as the indicator, a 13px
 * description line, and the «Рекомендуем» badge as a small primary GPill.
 * Rows, not pills: pills stay with the follow-up suggestions (drift item 8).
 */
import { GPill } from "@garden/ui";
import { Check } from "lucide-react";
import type { KeyboardEvent, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";

import type { ClarifySkin } from "./skin";

export interface ClarifyOptionRowProps {
  role: "radio" | "checkbox";
  checked: boolean;
  label: string;
  desc?: string | null;
  recommended?: boolean;
  /** A glyph before the label (the ✎ of «Свой ответ…»). */
  leading?: ReactNode;
  testId: string;
  tabIndex: number;
  disabled: boolean;
  skin: ClarifySkin;
  buttonRef: (element: HTMLButtonElement | null) => void;
  onActivate: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

export function ClarifyOptionRow({
  role,
  checked,
  label,
  desc,
  recommended = false,
  leading,
  testId,
  tabIndex,
  disabled,
  skin,
  buttonRef,
  onActivate,
  onFocus,
  onKeyDown,
}: ClarifyOptionRowProps): ReactElement {
  const { t } = useTranslation("chat");
  return (
    <button
      ref={buttonRef}
      type="button"
      role={role}
      aria-checked={checked}
      tabIndex={tabIndex}
      disabled={disabled}
      data-testid={testId}
      onClick={onActivate}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      className={[
        "flex min-h-11 w-full items-start gap-3 rounded-[var(--g-radius-sm)] border px-4 py-3 text-left",
        "transition-colors duration-150 motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        skin.ring,
        checked ? skin.rowOn : skin.row,
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "mt-0.5 grid size-[18px] shrink-0 place-items-center border-2",
          role === "radio" ? "rounded-full" : "rounded-[4px]",
          checked ? skin.markOn : skin.mark,
        ].join(" ")}
      >
        {checked ? (
          role === "radio" ? (
            <span className="size-1.5 rounded-full bg-current" />
          ) : (
            <Check {...iconProps("xs")} strokeWidth={3} />
          )
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[16px] font-medium leading-snug">
          {leading}
          {label}
          {recommended ? (
            <GPill tone="primary" size="sm" active={checked}>
              {t("clarify.recommended")}
            </GPill>
          ) : null}
        </span>
        {desc ? (
          <span className={["mt-0.5 block text-[14px] leading-snug", checked ? skin.descOn : skin.desc].join(" ")}>
            {desc}
          </span>
        ) : null}
      </span>
    </button>
  );
}
